import React from 'react';
import { useSlideGesture } from './use-slide-gesture';
import { SlideSpring, useSliderSprings } from './use-slider-springs';

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

export type OffsetChangeSource = 'external' | 'drag';
export type OffsetChangeDirection = 'previous' | 'next';

export interface SliderProps {
  readonly offset: number;
  readonly sliderWidth: number;
  readonly totalSlideCount: number;
  readonly visibleSlideCount?: number;
  readonly disableDrag?: boolean;
  readonly onDrag?: (relativeDelta: number) => void;

  readonly onOffsetChange: (offset: number, direction: OffsetChangeDirection, source: OffsetChangeSource) => void;

  readonly onStart?: () => void;
  readonly onRest?: () => void;
}

export interface SliderResult {
  readonly pageIndex: number;
  readonly pageCount: number;
  readonly springs: SlideSpring[];
  readonly setSpringProps: (relativeDelta: number) => void;
  readonly bindGestureHandlers: () => React.DOMAttributes<HTMLElement>;
  readonly previous: (() => void) | undefined;
  readonly next: (() => void) | undefined;
}

export function useSlider({
  sliderWidth,
  totalSlideCount,
  visibleSlideCount = 1,
  offset,
  disableDrag,
  onDrag,
  onOffsetChange,
  onStart,
  onRest,
}: SliderProps): SliderResult {
  const enabled = sliderWidth > 0 && totalSlideCount > visibleSlideCount;
  const maxOffset = enabled ? totalSlideCount - visibleSlideCount : 0;
  const clampedOffset = clamp(offset, 0, maxOffset);
  const pageIndex = Math.ceil(clampedOffset / visibleSlideCount);
  const pageCount = Math.ceil(totalSlideCount / visibleSlideCount);
  const slideWidth = sliderWidth / visibleSlideCount;
  const dragThreshold = slideWidth / 3;
  const offsetRef = React.useRef(clampedOffset);

  // Reflect the offset in a ref to always use the current value when dragging.
  offsetRef.current = clampedOffset;

  const handleOffsetChange = React.useCallback(
    (newOffset: number, direction: OffsetChangeDirection, source: OffsetChangeSource) => {
      const newClampedOffset = clamp(newOffset, 0, maxOffset);

      if (clampedOffset !== newClampedOffset) {
        onOffsetChange(newClampedOffset, direction, source);
      }
    },
    [clampedOffset, maxOffset, onOffsetChange]
  );

  const [springs, setSpringProps] = useSliderSprings({
    offsetRef,
    slideWidth,
    sliderWidth,
    totalSlideCount,
    visibleSlideCount,
    immediate: disableDrag,
    onStart,
    onRest,
  });

  const bindGestureHandlers = useSlideGesture({
    enabled,
    sliderWidth,
    dragThreshold,

    onDrag: React.useCallback(
      (relativeDelta) => {
        if (disableDrag) {
          return;
        }

        setSpringProps(relativeDelta);

        if (onDrag) {
          onDrag(relativeDelta);
        }
      },
      [setSpringProps, onDrag]
    ),

    onDragThresholdExceeded: React.useCallback(
      (direction) => {
        const stopLeft = clampedOffset === 0 && direction === 'right';
        const stopRight = clampedOffset === maxOffset && direction === 'left';

        if (disableDrag || stopLeft || stopRight) {
          return false;
        }

        const offsetDelta = direction === 'left' ? 1 : -1;

        handleOffsetChange(clampedOffset + offsetDelta, offsetDelta === 1 ? 'next' : 'previous', 'drag');

        return true;
      },
      [clampedOffset, disableDrag, handleOffsetChange]
    ),
  });

  const previous = React.useCallback(() => {
    handleOffsetChange(clampedOffset - visibleSlideCount, 'previous', 'external');
  }, [handleOffsetChange, clampedOffset, visibleSlideCount]);

  const next = React.useCallback(() => {
    handleOffsetChange(clampedOffset + visibleSlideCount, 'next', 'external');
  }, [handleOffsetChange, clampedOffset, visibleSlideCount]);

  React.useLayoutEffect(() => {
    setSpringProps();
  }, [sliderWidth, visibleSlideCount, clampedOffset]);

  return {
    pageIndex,
    pageCount,
    springs,
    setSpringProps,
    bindGestureHandlers,
    previous: clampedOffset > 0 ? previous : undefined,
    next: clampedOffset < maxOffset ? next : undefined,
  };
}
